/**
 * Wraps the GPU picking functionality in a cache. The pick results for a point will be cached for a frame so that
 * readPixels will only get called once for that point per frame.
 */
export class PickingCache {
    constructor(renderer) {
        /**
         * Pick World Space Cache
         * @private
         */
        this.wsCache = new Map();
        /**
         * Pick View Space Cache
         * @private
         */
        this.vsCache = new Map();
        /**
         * Pick World Space Snapping Cache
         * @private
         */
        this.wssCache = new Map();
        /**
         * Pick View Space Snapping Cache
         * @private
         */
        this.vssCache = new Map();
        this.renderer = renderer;
    }
    /**
     * Should be called at the beginning of every frame. Invalidates the pick cache (removes all the entries) so we can
     * get new values.
     */
    newFrame() {
        this.wsCache.clear();
        this.vsCache.clear();
        this.wssCache.clear();
        this.vssCache.clear();
    }
    /**
     * @see Renderer.pickScreenWorldSpace
     * @param point
     */
    pickScreenWorldSpace(point) {
        return this.getWithDefaultInitializer(point, this.wsCache, (p) => this.renderer.pickScreenWorldSpaceImpl(p));
    }
    /**
     * @see Renderer.pickScreenViewSpace
     * @param point
     */
    pickScreenViewSpace(point) {
        return this.getWithDefaultInitializer(point, this.vsCache, (p) => this.renderer.pickScreenViewSpaceImpl(p));
    }
    /**
     * @see Renderer.pickScreenWorldSpaceSnapping
     * @param point
     */
    pickScreenWorldSpaceSnapping(point) {
        return this.getWithDefaultInitializer(point, this.wssCache, (p) => this.renderer.pickScreenWorldSpaceSnappingImpl(p));
    }
    /**
     * @see Renderer.pickScreenViewSpaceSnapping
     * @param point
     */
    pickScreenViewSpaceSnapping(point) {
        return this.getWithDefaultInitializer(point, this.vssCache, (p) => this.renderer.pickScreenViewSpaceSnappingImpl(p));
    }
    /**
     * Internal function that implements the caching layer for any arbitrary picking function. Gets used by the public
     * interface to implement each picking variant without duplicating code.
     * @param point
     * @param cache
     * @param initializer
     * @private
     */
    getWithDefaultInitializer(point, cache, initializer) {
        const hash = this.hashPoint(point);
        const item = cache.get(hash);
        if (item !== undefined) {
            return item;
        }
        else {
            const result = initializer(point);
            cache.set(hash, result);
            return result;
        }
    }
    /**
     * Returns the spatial hash of the given screen coordinate. Will produce a unique hash for all integer points within
     * the range (0,0) (65535, 65535). Points outside this range may produce hash collisions.
     * @param point
     * @private
     */
    hashPoint(point) {
        return 65536 * point.y + point.x;
    }
}
